import type { CurrentUser, UserPreferences, UserPreferencesPatch } from '@techgitter/contracts';

const API_URL = '/api/me';

/**
 * Resolve the calling user's identity + roles from the backend.
 *
 * Throws on transport errors. Returns null when the backend responds 404
 * (authenticated to AD but not provisioned in cr001_userses) so the caller
 * can render the "not provisioned" branch separately from a hard failure.
 */
export async function fetchMe(signal?: AbortSignal): Promise<CurrentUser | null> {
  const response = await fetch(API_URL, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.text();
      detail = body ? `: ${body.slice(0, 200)}` : '';
    } catch {
      // ignore
    }
    throw new Error(`Failed to fetch /api/me (${response.status} ${response.statusText})${detail}`);
  }

  const data = await response.json();
  return data as CurrentUser;
}

/**
 * Update the signed-in user's own preferences (`PATCH /api/me/preferences`).
 * Sends only the keys given; resolves to the full, merged preferences.
 */
export async function updateMyPreferences(
  patch: UserPreferencesPatch,
  signal?: AbortSignal,
): Promise<UserPreferences> {
  const response = await fetch('/api/me/preferences', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(patch),
    signal,
  });
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = typeof body?.error === 'string' ? body.error : '';
    } catch {
      // ignore
    }
    throw new Error(detail || `Couldn't save your preferences (${response.status})`);
  }
  return (await response.json()) as UserPreferences;
}
